// Stage 2D-2: freelancer costs (paid now, bill to pay later, pay a bill) and an expense or asset paid personally by a
// director or shareholder. Entered manually by an Admin; nothing is read from, linked to or created by any operational
// module. The database builds the debit/credit lines (acc_save_transaction → acc_2d2_prepare) and enforces every rule.
import { useState, useEffect, useMemo, useRef } from "react";
import { Modal, Button, Field, Input, Select, TextArea, Notice, DocumentUploader } from "./kit/components";
import { T, text, PAYMENT_METHODS } from "./kit/tokens";
import { showToast, useMobile } from "../ui/core";
import { previewTransaction, saveTransaction, attachFile, loadFreelancerBills } from "./api";
import { DualDateInput, errText } from "./shared";
import { BS_MONTHS } from "./bs";
import { npr, todayIso, partyRoleMatch, FREELANCER_MODES, partyPayload, partyProblems, ownerPaidCategories, ownLoanAccount } from "./workflow";

const EMPTY = { txn_date: "", amount: "", party_id: "", bill_id: "", counter_account_id: "", reference: "", description: "", operational_ref: "",
    relates_from: "", relates_to: "", relates_note: "", money_account_id: "", payment_date: "", payment_method: "bank_transfer", due_date: "" };

function Choice({ active, title, sub, onClick }) {
    return (
        <button type="button" onClick={onClick} aria-pressed={active}
            style={{ textAlign: "left", background: active ? T.surface2 : T.surface, border: `1px solid ${active ? T.primary : T.border}`, borderRadius: T.radius,
                padding: "12px 14px", cursor: "pointer", fontFamily: T.font, display: "grid", gap: 4, borderLeft: `3px solid ${active ? T.primary : T.border}` }}>
            <span style={{ fontSize: 14, fontWeight: 650, color: T.textStrong }}>{title}</span>
            {sub && <span style={{ fontSize: 12.5, color: T.textMuted, lineHeight: 1.45 }}>{sub}</span>}
        </button>
    );
}
function MonthPick({ value, onChange, years, label }) {
    const [y, m] = (value || "").split("-");
    const set = (ny, nm) => onChange(ny && nm ? `${ny}-${String(nm).padStart(2, "0")}` : "");
    return (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
            <Select aria-label={`${label} month`} value={m ? Number(m) : ""} onChange={e => set(y || years[years.length - 1], e.target.value)}>
                <option value="">—</option>{BS_MONTHS.map((n, i) => <option key={n} value={i + 1}>{n}</option>)}
            </Select>
            <Select aria-label={`${label} year`} value={y || ""} onChange={e => set(e.target.value, m || 1)}>
                <option value="">Year…</option>{years.map(v => <option key={v} value={v}>{v}</option>)}
            </Select>
        </div>
    );
}
function Preview({ preview, err, note }) {
    const box = { background: T.surface2, border: `1px solid ${T.borderSoft}`, borderRadius: T.radius, padding: 14, display: "grid", gap: 10 };
    if (err) return <div style={box}><b style={{ fontSize: 13, color: T.textStrong }}>What this does to the books</b><span style={{ fontSize: 13, color: T.warning }}>{err}</span></div>;
    if (!preview) return <div style={box}><b style={{ fontSize: 13, color: T.textStrong }}>What this does to the books</b><span style={text.caption}>Fill in the details to see the accounting.</span></div>;
    return (
        <div style={box}>
            <b style={{ fontSize: 13, color: T.textStrong }}>What this does to the books</b>
            {note && <span style={{ fontSize: 13, color: T.text }}>{note}</span>}
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead><tr>{["Account", "Debit", "Credit"].map((h, i) => <th key={h} style={{ textAlign: i ? "right" : "left", color: T.textMuted, fontWeight: 600, fontSize: 11, padding: "4px 0", borderBottom: `1px solid ${T.border}` }}>{h}</th>)}</tr></thead>
                <tbody>{(preview.lines || []).map((l, i) => (
                    <tr key={i}>
                        <td style={{ padding: "6px 0", color: T.text }}>{l.code} {l.name}{l.party ? <span style={{ color: T.textMuted }}> · {l.party}</span> : null}</td>
                        <td style={{ textAlign: "right", ...text.money, fontSize: 13 }}>{Number(l.debit) ? npr(l.debit) : ""}</td>
                        <td style={{ textAlign: "right", ...text.money, fontSize: 13 }}>{Number(l.credit) ? npr(l.credit) : ""}</td>
                    </tr>))}</tbody>
            </table>
            <span style={text.caption}>Gets a {preview.series} voucher number when it is posted.</span>
            {(preview.duplicates || []).length > 0 && <span style={{ fontSize: 12, color: T.warning }}>Looks similar to: {preview.duplicates.map(d => d.display).join("; ")}</span>}
        </div>
    );
}

// Shared state, preview and saving for both forms.
function usePartyForm({ open, ctx, initial, kind, onSaved }) {
    const { data } = ctx;
    const [f, setF] = useState(EMPTY);
    const [files, setFiles] = useState([]);
    const [preview, setPreview] = useState(null);
    const [previewErr, setPreviewErr] = useState("");
    const [dup, setDup] = useState(null);
    const [dupReason, setDupReason] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const reqId = useRef(null);
    useEffect(() => {
        if (!open) return;
        reqId.current = crypto.randomUUID();
        const t = initial || {};
        const firstBank = data.moneyAccounts.find(m => m.active && m.kind === "bank") || data.moneyAccounts.find(m => m.active);
        setF({ ...EMPTY, ...Object.fromEntries(Object.keys(EMPTY).map(k => [k, t[k] == null ? EMPTY[k] : String(t[k])])),
            txn_date: t.txn_date || todayIso(), money_account_id: t.money_account_id || firstBank?.id || "", payment_method: t.payment_method || "bank_transfer" });
        setFiles([]); setPreview(null); setPreviewErr(""); setDup(null); setDupReason(""); setErr(""); setBusy(false);
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
    const set = (k) => (e) => { const v = e?.target ? e.target.value : e; setF(x => ({ ...x, [k]: v })); setDup(null); };
    const payload = () => ({ ...partyPayload(f, kind), id: initial?.id || null, expected_updated_at: initial?.updated_at || null });
    // Preview only once the required basics are in (a pre-filled amount must not trigger a premature "enter a reference").
    const ready = kind && Number(f.amount) > 0 && (f.reference || "").trim() && (kind === "freelancer_bill_payment" ? f.bill_id : f.party_id);
    useEffect(() => {
        if (busy) return;
        if (!ready) { setPreview(null); setPreviewErr(""); return; }
        const t = setTimeout(() => {
            previewTransaction(payload()).then(p => { setPreview(p); setPreviewErr(""); }).catch(e => { setPreview(null); setPreviewErr(errText(e)); });
        }, 350);
        return () => clearTimeout(t);
    }, [kind, f, busy]); // eslint-disable-line react-hooks/exhaustive-deps
    const save = async (markReady) => {
        setErr("");
        const problems = partyProblems(f, kind);
        if (problems.length) { setErr(problems[0]); return; }
        setBusy(true);
        try {
            const res = await saveTransaction({ ...payload(), client_request_id: reqId.current, mark_ready: markReady, duplicate_reason: dup ? dupReason.trim() || null : null });
            for (const file of files) await attachFile(res.entry_id, file);
            showToast(markReady ? "Sent for review" : "Draft saved");
            await onSaved(res);
            return;
        } catch (e) {
            const m = errText(e);
            if (m.startsWith("POSSIBLE_DUPLICATE")) { setDup(m.replace(/^POSSIBLE_DUPLICATE:\s*/, "")); setErr(""); }
            else setErr(m);
        }
        setBusy(false);
    };
    return { f, setF, set, files, setFiles, preview, previewErr, dup, dupReason, setDupReason, busy, err, save };
}

function CommonFields({ s, ctx, editing, paid, showDue }) {
    const { data, cal, display } = ctx;
    const mobile = useMobile();
    const years = useMemo(() => { const ys = cal.years.slice().sort(); return ys.length ? ys : [2082, 2083]; }, [cal]);
    const { f, set } = s;
    return (<>
        <div style={{ display: "grid", gridTemplateColumns: mobile ? "1fr" : "1fr 1fr", gap: 10 }}>
            <Field label="Accounting date" required hint="Decides the accounting period."><DualDateInput value={f.txn_date} onChange={set("txn_date")} cal={cal} display={display} /></Field>
            {paid ? <Field label="Payment date" required hint="When the money was paid. Information only."><DualDateInput value={f.payment_date} onChange={set("payment_date")} cal={cal} display={display} /></Field>
                : showDue ? <Field label="Due date" hint="Optional."><Input type="date" value={f.due_date} onChange={set("due_date")} /></Field> : <span />}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: mobile ? "1fr" : "1fr 1fr", gap: 10 }}>
            <Field label="Amount (NPR)" required>
                <Input type="number" inputMode="decimal" min="0" step="0.01" value={f.amount} onChange={set("amount")} style={{ fontSize: 16, fontWeight: 650, fontVariantNumeric: "tabular-nums" }} />
            </Field>
            {paid && <Field label="Paid from" required><Select value={f.money_account_id} onChange={set("money_account_id")}>
                <option value="">Choose…</option>{data.moneyAccounts.filter(m => m.active).map(m => <option key={m.id} value={m.id}>{m.display_name}</option>)}</Select></Field>}
        </div>
        {paid && <Field label="How it was paid"><Select value={f.payment_method} onChange={set("payment_method")}>{PAYMENT_METHODS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</Select></Field>}
        <Field label="Relates to" hint="Optional Nepali month(s) the cost is for. Information only: it never changes the accounting period.">
            <div style={{ display: "grid", gridTemplateColumns: mobile ? "1fr" : "1fr 1fr", gap: 8 }}>
                <MonthPick label="From" value={f.relates_from} years={years} onChange={set("relates_from")} />
                <MonthPick label="To" value={f.relates_to} years={years} onChange={set("relates_to")} />
            </div>
        </Field>
        <Field label="Reference" required hint="Bill, invoice, receipt or approval number."><Input value={f.reference} onChange={set("reference")} /></Field>
        <Field label="Operational reference" hint="Optional free text. Only a note: nothing is looked up or linked."><Input value={f.operational_ref} maxLength={200} onChange={set("operational_ref")} /></Field>
        <Field label="Description"><TextArea value={f.description} onChange={set("description")} placeholder="In your own words" /></Field>
        <DocumentUploader optimize label="Bill, receipt or approval (optional)" file={s.files[0] || null} onChange={(file) => s.setFiles(file ? [file] : [])} />
        {!editing && !s.files.length && <Notice tone="info">No evidence attached. The reference is required; a document is optional and can be added later. Review will show that none is attached.</Notice>}
    </>);
}
function SideColumn({ s, note }) {
    const mobile = useMobile();
    return (
        <div style={{ position: mobile ? "static" : "sticky", top: 0, display: "grid", gap: 12 }}>
            <Preview preview={s.preview} err={s.previewErr} note={note} />
            {s.dup && (
                <div role="alert" style={{ border: `2px solid ${T.warning}`, background: T.warningBg, borderRadius: T.radius, padding: 14, display: "grid", gap: 8 }}>
                    <b style={{ color: T.warning }}>This may already be recorded</b>
                    <span style={{ fontSize: 13, color: T.text }}>Similar: {s.dup.replace(/ — give a reason.*$/, "")}</span>
                    <span style={{ fontSize: 13, color: T.text }}>If it really is separate, say why. The reason is kept in the activity log.</span>
                    <TextArea value={s.dupReason} onChange={e => s.setDupReason(e.target.value)} placeholder="e.g. Second instalment of the same amount" />
                </div>
            )}
            {s.err && <Notice tone="danger">{s.err}</Notice>}
        </div>
    );
}
const footer = (s, onClose, back) => <>
    {back}<span style={{ flex: 1 }} />
    <Button variant="secondary" onClick={onClose} disabled={s.busy}>Cancel</Button>
    <Button variant="secondary" onClick={() => s.save(false)} disabled={s.busy || (!!s.dup && !s.dupReason.trim())}>Save draft</Button>
    <Button onClick={() => s.save(true)} disabled={s.busy || (!!s.dup && !s.dupReason.trim())}>{s.busy ? "Saving…" : "Save & send for review"}</Button>
</>;

export function FreelancerForm({ open, ctx, initial, onClose, onSaved }) {
    const { data } = ctx;
    const mobile = useMobile();
    const editing = !!initial?.id;
    const [kind, setKind] = useState("");
    const [step, setStep] = useState("choose");
    const [bills, setBills] = useState([]);
    useEffect(() => { if (open) { setKind(initial?.kind || ""); setStep(initial?.kind ? "details" : "choose"); } }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
    const s = usePartyForm({ open, ctx, initial, kind, onSaved });
    const rolesOf = useMemo(() => { const m = {}; data.roles.forEach(r => { (m[r.party_id] ||= []).push(r.role); }); return m; }, [data.roles]);
    const freelancers = data.parties.filter(p => p.active && partyRoleMatch(rolesOf[p.id] || [], "freelancer"));
    useEffect(() => {
        if (!open || kind !== "freelancer_bill_payment") return;
        loadFreelancerBills().then(rows => setBills(rows.filter(b => b.entry_status === "posted" && !b.is_reversed
            && Number(b.bill_outstanding) - Number(b.bill_pending) + (initial?.id && b.id === initial.bill_id ? Number(initial.amount) : 0) > 0))).catch(() => setBills([]));
    }, [open, kind]); // eslint-disable-line react-hooks/exhaustive-deps
    const bill = bills.find(b => b.id === s.f.bill_id);
    useEffect(() => { if (bill && s.f.amount === "" && !editing) s.setF(x => ({ ...x, amount: String(Number(bill.bill_outstanding) - Number(bill.bill_pending)) })); }, [bill]); // eslint-disable-line react-hooks/exhaustive-deps
    const label = (FREELANCER_MODES.find(([k]) => k === kind) || [])[1] || "";
    return (
        <Modal open={open} width={step === "details" ? 960 : 640} onClose={s.busy ? undefined : onClose}
            title={step === "choose" ? "Freelancer" : `Freelancer — ${label.toLowerCase()}`}
            subtitle={step === "choose" ? "Entered by hand. Nothing is taken from freelancer records or any other module. Costs go to 5100 Freelancer / Contractor Costs."
                : "The accounting date decides the period. The payment date and \"relates to\" are information only."}
            footer={step === "details" ? footer(s, onClose, !editing && <Button variant="ghost" onClick={() => setStep("choose")}>← Back</Button>)
                : <><span style={{ flex: 1 }} /><Button variant="secondary" onClick={onClose}>Cancel</Button><Button disabled={!kind} onClick={() => setStep("details")}>Continue</Button></>}>
            {step === "choose" && <div style={{ display: "grid", gap: 10 }}>{FREELANCER_MODES.map(([k, l, sub]) => <Choice key={k} active={kind === k} title={l} sub={sub} onClick={() => setKind(k)} />)}</div>}
            {step === "details" && (
                <div style={{ display: "grid", gridTemplateColumns: mobile ? "1fr" : "minmax(0,1.15fr) minmax(0,1fr)", gap: 20, alignItems: "start" }}>
                    <div style={{ display: "grid", gap: 14 }}>
                        {kind === "freelancer_bill_payment" ? (
                            <Field label="Which freelancer bill?" required hint={bills.length ? undefined : "No posted freelancer bill is waiting to be paid."}>
                                <Select value={s.f.bill_id} onChange={e => { const b = bills.find(x => x.id === e.target.value); s.setF(x => ({ ...x, bill_id: e.target.value, amount: b ? String(Number(b.bill_outstanding) - Number(b.bill_pending)) : x.amount })); }}>
                                    <option value="">Choose a bill…</option>
                                    {bills.map(b => <option key={b.id} value={b.id}>{b.party_name} · {b.display_no} · owed {npr(Number(b.bill_outstanding) - Number(b.bill_pending))}{b.reference ? ` · ${b.reference}` : ""}</option>)}
                                </Select>
                            </Field>
                        ) : (
                            <Field label="Freelancer" required hint={freelancers.length ? undefined : "Add the person under People & companies with the Freelancer role first."}>
                                <Select value={s.f.party_id} onChange={s.set("party_id")}><option value="">Choose…</option>{freelancers.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}</Select>
                            </Field>
                        )}
                        {bill && <div style={text.caption}>Still owed on this bill: <b style={{ color: T.textStrong }}>{npr(Number(bill.bill_outstanding) - Number(bill.bill_pending))}</b></div>}
                        <CommonFields s={s} ctx={ctx} editing={editing} paid={kind !== "freelancer_bill"} showDue={kind === "freelancer_bill"} />
                    </div>
                    <SideColumn s={s} note={kind === "freelancer_bill" ? "No money moves yet. This records the cost and what the company owes the freelancer."
                        : kind === "freelancer_bill_payment" ? "Not a new cost: it pays what is owed on the bill." : "The cost and its payment, in one entry."} />
                </div>
            )}
        </Modal>
    );
}

export function DirectorPaidForm({ open, ctx, initial, onClose, onSaved }) {
    const { data } = ctx;
    const mobile = useMobile();
    const editing = !!initial?.id;
    const kind = "owner_paid_expense";
    const s = usePartyForm({ open, ctx, initial, kind, onSaved });
    const rolesOf = useMemo(() => { const m = {}; data.roles.forEach(r => { (m[r.party_id] ||= []).push(r.role); }); return m; }, [data.roles]);
    const owners = data.parties.filter(p => p.active && partyRoleMatch(rolesOf[p.id] || [], "owner"));
    const cats = useMemo(() => ownerPaidCategories(data), [data]);
    const loan = s.f.party_id ? ownLoanAccount(data, s.f.party_id) : null;
    const who = owners.find(p => p.id === s.f.party_id)?.name;
    return (
        <Modal open={open} width={960} onClose={s.busy ? undefined : onClose} title="Expense paid personally by a director or shareholder"
            subtitle="No company money moves: the amount is recorded as owed to that person on their own loan account. Entered by hand; nothing is inferred."
            footer={footer(s, onClose)}>
            <div style={{ display: "grid", gridTemplateColumns: mobile ? "1fr" : "minmax(0,1.15fr) minmax(0,1fr)", gap: 20, alignItems: "start" }}>
                <div style={{ display: "grid", gap: 14 }}>
                    <Field label="Director or shareholder who paid" required>
                        <Select value={s.f.party_id} onChange={s.set("party_id")}><option value="">Choose…</option>{owners.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}</Select>
                    </Field>
                    {s.f.party_id && (loan ? <div style={text.caption}>Recorded as owed to {who} on <b style={{ color: T.textStrong }}>{loan.code} {loan.name}</b> (their own loan account).</div>
                        : <Notice tone="warning">This person has no single loan account of their own. Set it up under People & companies first.</Notice>)}
                    <Field label="What was paid for" required hint="An expense or an asset. Salary, bonus and commission are recorded with Staff costs.">
                        <Select value={s.f.counter_account_id} onChange={s.set("counter_account_id")}><option value="">Choose…</option>{cats.map(a => <option key={a.id} value={a.id}>{a.code} {a.name}</option>)}</Select>
                    </Field>
                    <CommonFields s={s} ctx={ctx} editing={editing} paid={false} showDue={false} />
                </div>
                <SideColumn s={s} note="No bank, cash or wallet line: company money balances do not change." />
            </div>
        </Modal>
    );
}
